import {
  Box, Divider, Drawer, useMediaQuery, useTheme,
} from '@mui/material';
import React, { useContext } from 'react';
import NavContext from '../../contexts/NavContext';
import MenuItem from './MenuItem';
import SideMenuItem from './SidebarMenuItem';
import Logo from '../Logo/Logo';
import { getHelpCenterLink } from '../../helpers/zendeskHelpers';
import HelpMenuItem from './HelpMenuItem/HelpMenuItem';

export type Tracker = 'timetracker' | 'administrator';

type MenuGroups = MenuItem[];

interface SidebarMenuProps {
  app: Tracker;
  groupedMenus?: MenuGroups[];
  onBrandingClicked?: () => void;
  helpCenter?: boolean;
  // eslint-disable-next-line no-unused-vars
  onRouteSelected: (menuItem: MenuItem) => void;
  footer?: Omit<MenuItem, 'menuItems' | 'hidden'>;
}

const SidebarMenu: React.FC<React.PropsWithChildren<SidebarMenuProps>> = (props) => {
  const navContext = useContext(NavContext);
  const theme = useTheme();
  const bigDevice = useMediaQuery(theme.breakpoints.up('md'));
  const compact = bigDevice && !navContext.navOpen;

  const {
    app, helpCenter, footer, groupedMenus, onBrandingClicked, onRouteSelected,
  } = props;

  // eslint-disable-next-line no-unused-vars
  const onHelpClicked = (menuItem: MenuItem) => {
    window.open(getHelpCenterLink());
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let selectedLogo: any;
  if (bigDevice && !navContext.navOpen) {
    selectedLogo = 'icon-logo';
  } else if (bigDevice && navContext.navOpen && app === 'timetracker') {
    selectedLogo = 'caption-logo';
  } else if (bigDevice && navContext.navOpen && app === 'administrator') {
    selectedLogo = 'admin-logo';
  } else {
    selectedLogo = 'full-logo';
  }

  const groupLength = groupedMenus?.length;

  const menuList = groupedMenus?.map((menus, i) => (
    <React.Fragment key={`menu list item-${menus[0].title}`}>
      {
        menus.map((item) => (
          !item.hidden && (
            <SideMenuItem
              key={item.title}
              navOpen={navContext.navOpen}
              bigDevice={bigDevice}
              menuItem={item}
              onRouteSelected={onRouteSelected}
            />
          )
        ))
      }
      {
        (groupLength && !((i + 1) >= groupLength)) && (
          <Divider
            light
            sx={{
              marginTop: theme.spacing(2),
              marginBottom: theme.spacing(2),
              height: '2px',
              color: theme.palette.action.selected,
            }}
          />
        )
      }
    </React.Fragment>
  ));

  return (
    <Drawer
      anchor="left"
      variant={bigDevice ? 'permanent' : 'temporary'}
      open={navContext.navOpen}
      onClose={() => navContext.setNav(false)}
      PaperProps={{
        sx: {
          position: 'relative',
          width: 'fit-content',
          backgroundImage: 'none',
        },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          overflow: 'auto',
          width: '75vw',
          maxWidth: theme.spacing(40),
          padding: theme.spacing(2),
          paddingTop: theme.spacing(0),
          boxSizing: 'border-box',
          ...(compact && {
            width: theme.spacing(9),
            alignItems: 'center',
            padding: theme.spacing(1.5),
            paddingTop: theme.spacing(0),
          }),
        }}
      >
        <Box
          component="header"
          sx={{
            display: 'flex',
            flexGrow: 0,
            paddingBottom: theme.spacing(2),
            paddingLeft: theme.spacing(1.5),
            paddingTop: theme.spacing(1.5),
            borderBottom: `2px solid ${theme.palette.action.selected}`,
            height: theme.spacing(7),
            boxSizing: 'border-box',
            [theme.breakpoints.up('md')]: {
              height: theme.spacing(8),
              paddingTop: theme.spacing(2),
            },
            ...(compact && {
              alignItems: 'center',
              paddingLeft: theme.spacing(0),
            }),
          }}
        >
          <Box
            onClick={onBrandingClicked}
            role="navigation"
            sx={{
              cursor: 'pointer',
              height: '100%',
            }}
          >
            <Logo
              logoType={selectedLogo}
              styles={{
                fill: theme.palette.text.primary,
              }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            padding: `${theme.spacing(2)} ${theme.spacing(0)}`,
          }}
        >
          {menuList}
        </Box>
        {
          footer && (
            <Box
              component="footer"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 0,
                // height: theme.spacing(8),
                paddingTop: `calc(${theme.spacing(2)} - 2px)`,
                borderTop: `2px solid ${theme.palette.action.selected}`,
              }}
            >
              {helpCenter && (
                <HelpMenuItem
                  navOpen={navContext.navOpen}
                  bigDevice={bigDevice}
                  onRouteSelected={onHelpClicked}
                />
              )}

              <SideMenuItem
                navOpen={navContext.navOpen}
                bigDevice={bigDevice}
                menuItem={{ ...footer, menuItems: undefined, hidden: false }}
                onRouteSelected={onRouteSelected}
              />
            </Box>
          )
        }
      </Box>
    </Drawer>
  );
};

export default SidebarMenu;
